import {DateTime as LuxonDateTime} from 'luxon'
import {DateTimeOptions, DateTimeUnit} from "luxon/src/datetime";
import {DurationLike} from "luxon/src/duration";
import {Zone} from "luxon/src/zone";


export class Time{
    readonly hour: string;
    readonly minute: string;

    private constructor(hour: number, minute: number) {
        this.hour = hour.toString().padStart(2, '0');
        this.minute = minute.toString().padStart(2, '0');
    }

    static fromString(value: string): Time| null {
        const acceptedFormats = ['h:mm a', 'h:m a', 'hh:m', 'h mm a', 'h m a', 'hh m', 'h:m']

        const parsedStrings = acceptedFormats.map((format) => LuxonDateTime.fromFormat(value, format)
        )

        const valid = parsedStrings.find( formats => formats.isValid)

        if (valid == undefined) {
            return null
        }

        return new Time(valid.hour, valid.minute);
    }

    asDateTime(): DateTime {
        const now = LuxonDateTime.local();
        const dt = now.set({
            hour: parseInt(this.hour, 10),
            minute: parseInt(this.minute, 10),
            second: 0,
            millisecond: 0,
        });
        return DateTime.fromRaw(dt);
    }

    asDateString() : string {
        return this.hour+":"+this.minute
    }

}

export class TimeOfDay {
    readonly value: string
    private constructor(value: string){
        this.value = value
    }

    static fromString(value: string): TimeOfDay {
        return new TimeOfDay(value)
    }
}

export class DateTime {
    private readonly dateTime: LuxonDateTime;

    get invalidReason(): string {
        return this.dateTime.invalidReason ?? ''
    }

    private constructor(value: LuxonDateTime, throwOnInvalid = true) {
        if (throwOnInvalid && !value.isValid) {
            throw Error(`Invalid Date: ${value.invalidReason} - ${value.invalidExplanation}`)
        }
        this.dateTime = value
    }

    raw(): LuxonDateTime{
        return this.dateTime
    }

    static now(): DateTime {
        return new DateTime(LuxonDateTime.now())
    }

    static fromISODateString(text: string, zone = 'Europe/London'): DateTime {
        const value = LuxonDateTime.fromFormat(text, "yyyy-MM-dd", {zone: zone});
        return new DateTime(value)
    }

    static fromISO(value: string, opts: DateTimeOptions = {zone: 'Europe/London'}): DateTime {
        return new DateTime(LuxonDateTime.fromISO(value, opts))
    }

    static FromISOOrError(value: string, opts?: DateTimeOptions): DateTime | string {
        try{
            return new DateTime(LuxonDateTime.fromISO(value, opts))
        } catch(e){
            return `couldnt parse ${value}`
        }
    }

    static fromJSDate(value: Date, options?: { zone?: string | Zone }): DateTime {
        return new DateTime(LuxonDateTime.fromJSDate(value, options))
    }

    static local(): DateTime {
        return new DateTime(LuxonDateTime.local())
    }

    static fromSeconds(date: number) {
        return new DateTime(LuxonDateTime.fromSeconds(date))
    }

    static fromRaw(value: LuxonDateTime, throwOnError = true): DateTime {
        return new DateTime(value, throwOnError)
    }

    toLondonTimeZone(): DateTime {
        return new DateTime(this.dateTime.setZone("Europe/London"))
    }

    toISODate(): string {
        return this.dateTime.toISODate()!
    }


    toUTC(): DateTime {
        return new DateTime(this.dateTime.toUTC())
    }

    toISO(): string {
        return this.dateTime.toISO()!
    }

    toInputValue(): string {
        const toISO = this.toISO()
        if (toISO == null) throw new Error('date time was not valid' + this.invalidReason)
        return toISO.substring(0, 16)
    }

    plus(duration: DurationLike): DateTime {
        return new DateTime(this.dateTime.plus(duration));
    }

    toMillis(): number {
        return this.dateTime.toMillis()
    }

    toFormat(format: string) {
        return this.dateTime.toFormat(format)
    }

    toUnixInteger(): number {
        return this.dateTime.toUnixInteger()
    }

    endOf(unit: DateTimeUnit) {
        return new DateTime(this.dateTime.endOf(unit))
    }

    minus(duration: DurationLike) {
        return new DateTime(this.dateTime.minus(duration));
    }

    diffMinutes(other: DateTime): number{
        const minutes = this.dateTime.diff(other.dateTime,['minutes']).toObject().minutes;
        if(minutes == undefined) throw new Error(`diffing ${this.dateTime} and ${other.dateTime} is undefined`)
        return minutes
    }

    startOf(unit: DateTimeUnit) {
        return new DateTime(this.dateTime.startOf(unit));
    }

    get minute() {
        return this.dateTime.minute
    }
    get hour() {
        return this.dateTime.hour
    }
    get second() {
        return this.dateTime.second
    }

    isAfter(other: DateTime) {
        return this.raw() > other.raw();
    }
}
